import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { FAQS as GLOBAL_FAQS } from "@/data/faqs";
import { FAQ } from "@/constants/testIds";

// These questions are relevant to SAP, but not Workday course pages.
const WORKDAY_EXCLUDED_FAQ_QUESTIONS = new Set([
  "does zarantech offer sap training?",
  "does zarantech offer sap ai and sap joule training?",
]);

export function isExcludedWorkdayFaq(faq) {
  return WORKDAY_EXCLUDED_FAQ_QUESTIONS.has(
    String(faq?.q || faq?.question || "").trim().toLowerCase()
  );
}

export default function FaqSection({
  faqs,
  isWorkdayCourse = false,
  eyebrow = "09 · Frequently asked",
  heading = "Answers before you enrol.",
  testidRoot,
  align = "left",
  // Kept at the course-page width by default; the homepage overrides this
  // to max-w-[1440px] to match its other sections.
  maxWidthClass,
  // Kept at the course-page padding by default; the homepage overrides
  // this to match its other sections' tighter spacing.
  paddingClass = "py-12 lg:py-15",
}) {
  const sourceFaqs = Array.isArray(faqs) && faqs.length > 0 ? faqs : GLOBAL_FAQS;
  const list = isWorkdayCourse ? sourceFaqs.filter((faq) => !isExcludedWorkdayFaq(faq)) : sourceFaqs;
  if (!list || list.length === 0) return null;
  const isLeft = align === "left";
  const resolvedMaxWidthClass = maxWidthClass || (isLeft ? "max-w-[1200px]" : "max-w-[920px]");
  return (
    <section
      id="faq"
      data-testid={testidRoot || FAQ.root}
      className={`${paddingClass} bg-bg-alt scroll-mt-[120px] lg:scroll-mt-[136px]`}
      aria-labelledby="faq-title"
    >
      <div className={`mx-auto px-5 lg:px-10 ${resolvedMaxWidthClass}`}>
        <div className={isLeft ? "text-left" : "text-center"}>
          {isLeft ? (
            <span className="section-badge">{eyebrow}</span>
          ) : (
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              {eyebrow}
            </p>
          )}
          <h2
            id="faq-title"
            className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold text-ink mt-3 leading-[1.1] text-balance"
          >
            {heading}
          </h2>
        </div>

        <Accordion type="single" collapsible defaultValue="item-0" className="mt-10 rounded-2xl border border-line bg-white divide-y divide-line overflow-hidden">
          {list.map((f, i) => (
            <AccordionItem
              key={(f.q || f.question || "") + i}
              value={`item-${i}`}
              data-testid={testidRoot ? `${testidRoot}-item-${i}` : FAQ.item(i)}
              className="px-5 lg:px-7"
            >
              <AccordionTrigger className="py-5 text-left font-display text-[16px] lg:text-[17px] font-semibold text-ink hover:no-underline">
                {f.q || f.question}
              </AccordionTrigger>
              <AccordionContent className="pb-5 text-[14.5px] leading-relaxed text-ink-soft">
                {f.a || f.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
